import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, Button, Tooltip, Modal as AntModal} from "antd";
import { Canvas, IText, Circle, Rect, PencilBrush, Path, FabricObject, FabricImage, Triangle, Line, Polygon } from "fabric";

import { Room, RoomEvent, DataPacket_Kind, RemoteParticipant } from "livekit-client";
import { useRoomContext } from "@livekit/components-react";


type PaintModalProps = {
	isOpen: boolean;
};

type DrawingAction = {
	type: "path" | "circle" | "rect" | "text" | "clear" | "undo" | "modify" | "remove" | "sync" | "image" | "triangle" | "line" | "arrow" | "sticky";
	data?: any;
	objectId?: string;
	timestamp: number;
};

const FIXED_CANVAS_WIDTH = 1920;
const FIXED_CANVAS_HEIGHT = 1080;

const WhiteBoard = ({ isOpen }: PaintModalProps) => {
	const room = useRoomContext();
	const canvasRef = useRef<Canvas | null>(null);
	const canvasEl = useRef<HTMLCanvasElement | null>(null);
	const [color, setColor] = useState("#000000");
	const [strokeWidth, setStrokeWidth] = useState(3);
	const [persistedCanvasState, setPersistedCanvasState] = useState<any>(null);

	const isRemoteUpdate = useRef(false);
	const objectIdCounter = useRef(0);
	const throttleTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

	const generateObjectId = useCallback(() => {
		return `${room?.localParticipant?.identity || "local"}-${Date.now()}-${objectIdCounter.current++}`;
	}, [room]);




	const sendDrawingActionThrottled = useCallback(
		(action: DrawingAction, delay: number = 50) => {
			if (!room || isRemoteUpdate.current) return;

			const key = action.objectId || action.type;

			if (throttleTimers.current.has(key)) {
				clearTimeout(throttleTimers.current.get(key)!);
			}

			const timer = setTimeout(() => {
				try {
					const encoder = new TextEncoder();
					const data = encoder.encode(JSON.stringify(action));

				room.localParticipant?.publishData(
					data,
					DataPacket_Kind.RELIABLE,
					{ topic: "whiteboard" }
				);
				} catch (error) {
					console.error("Error sending drawing action:", error);
				}

				throttleTimers.current.delete(key);
			}, delay);

			throttleTimers.current.set(key, timer);
		},
		[room]
	);

	const sendDrawingAction = useCallback(
		(action: DrawingAction) => {
			if (!room || isRemoteUpdate.current) return;

			try {
				const encoder = new TextEncoder();
			const data = encoder.encode(JSON.stringify(action));

			room.localParticipant?.publishData(
				data,
				DataPacket_Kind.RELIABLE,
				{ topic: "whiteboard" }
			);
			} catch (error) {
				console.error("Error sending drawing action:", error);
			}
		},
		[room]
	);

	const getCanvasState = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return null;

		return {
			objects: canvas.getObjects().map((obj) => {
				const baseObj = obj.toObject() as any;
				const result: any = {
					...baseObj,
					objectId: (obj as any).objectId,
					type: obj.type,
				};
				
				if (obj.type === 'image') {
					result.src = (obj as any)._originalElement?.src || (obj as any).src;
				}
				
				return result;
			}),
		};
	}, []);

	const applyCanvasState = useCallback((state: any) => {
	
		const canvas = canvasRef.current;
		if (!canvas || !state) return;

		

		isRemoteUpdate.current = true;
		try {
			canvas.clear();
			canvas.backgroundColor = "#ffffff";

			state.objects.forEach((objData: any) => {
				let obj: FabricObject | null = null;

				if (objData.type === "path") {
					obj = new Path(objData.path, {
						stroke: objData.stroke,
						strokeWidth: objData.strokeWidth,
						fill: "",
						selectable: false,
						evented: false,
						objectId: objData.objectId,
					} as any);
					// Restore transformations for paths too
					if (objData.scaleX !== undefined) obj.set('scaleX', objData.scaleX);
					if (objData.scaleY !== undefined) obj.set('scaleY', objData.scaleY);
					if (objData.angle !== undefined) obj.set('angle', objData.angle);
					if (objData.left !== undefined) obj.set('left', objData.left);
					if (objData.top !== undefined) obj.set('top', objData.top);
				} else if (objData.type === "circle") {
					obj = new Circle({
						left: objData.left,
						top: objData.top,
						radius: objData.radius,
						stroke: objData.stroke,
						strokeWidth: objData.strokeWidth,
						fill: objData.fill || "transparent",
						selectable: true,
						evented: true,
						objectId: objData.objectId,
					} as any);
				} else if (objData.type === "rect") {
					obj = new Rect({
						left: objData.left,
						top: objData.top,
						width: objData.width,
						height: objData.height,
						stroke: objData.stroke,
						strokeWidth: objData.strokeWidth,
						fill: objData.fill || "transparent",
						selectable: true,
						evented: true,
						objectId: objData.objectId,
					} as any);
				} else if (objData.type === "i-text") {
					obj = new IText(objData.text || "Text", {
						left: objData.left,
						top: objData.top,
						fontSize: objData.fontSize,
						fill: objData.fill,
						editable: true,
						selectable: true,
						evented: true,
						objectId: objData.objectId,
					} as any);
				} else if (objData.type === "triangle") {
					obj = new Triangle({
						left: objData.left,
						top: objData.top,
						width: objData.width,
						height: objData.height,
						stroke: objData.stroke,
						strokeWidth: objData.strokeWidth,
						fill: objData.fill || "transparent",
						selectable: true,
						evented: true,
						objectId: objData.objectId,
					} as any);
				} else if (objData.type === "line") {
					obj = new Line([objData.x1, objData.y1, objData.x2, objData.y2], {
						stroke: objData.stroke,
						strokeWidth: objData.strokeWidth,
						selectable: true,
						evented: true,
						objectId: objData.objectId,
					} as any);
				} else if (objData.type === "image") {
					// Load image from URL
					if (objData.src) {
						FabricImage.fromURL(objData.src).then((img) => {
							img.set({
								left: objData.left,
								top: objData.top,
								scaleX: objData.scaleX,
								scaleY: objData.scaleY,
								angle: objData.angle || 0,
								selectable: true,
								evented: true,
								objectId: objData.objectId,
							} as any);
							canvas.add(img);
							canvas.renderAll();
						}).catch((err) => {
							console.error("Error loading image:", err);
						});
						return; 
					}
				}

				if (obj) {
					if (objData.scaleX !== undefined) obj.set('scaleX', objData.scaleX);
					if (objData.scaleY !== undefined) obj.set('scaleY', objData.scaleY);
					if (objData.angle !== undefined) obj.set('angle', objData.angle);
					if (objData.left !== undefined) obj.set('left', objData.left);
					if (objData.top !== undefined) obj.set('top', objData.top);
					
					obj.setCoords(); 
					canvas.add(obj);
				}
			});

			canvas.renderAll();
		} finally {
			isRemoteUpdate.current = false;
		}
	}, []);

	// Handle modal close - save canvas state before closing
	const handleCloseModal = useCallback(() => {
		if (canvasRef.current) {
			const state = getCanvasState();
			setPersistedCanvasState(state);
		}
	}, [getCanvasState]);

	const applyRemoteDrawingAction = useCallback(
		(action: DrawingAction) => {
			const canvas = canvasRef.current;
			if (!canvas) return;

	
			isRemoteUpdate.current = true;

			try {
				if (action.type === "sync") {
					applyCanvasState(action.data);
				} else if (action.type === "clear") {
					canvas.clear();
					canvas.backgroundColor = "#ffffff";
					canvas.renderAll();
				} else if (action.type === "undo") {
					const objects = canvas.getObjects();
					if (objects.length > 0) {
						canvas.remove(objects[objects.length - 1]);
						canvas.renderAll();
					}
				} else if (action.type === "remove" && action.objectId) {
					const obj = canvas.getObjects().find((o: any) => o.objectId === action.objectId);
					if (obj) {
						canvas.remove(obj);
						canvas.renderAll();
					}
				} else if (action.type === "modify" && action.objectId) {
					const obj = canvas.getObjects().find((o: any) => o.objectId === action.objectId);
					if (obj) {
						obj.set(action.data);
						obj.setCoords();
						canvas.renderAll();
					}
				} else if (action.type === "path") {
					const path = new Path(action.data.path, {
						stroke: action.data.stroke,
						strokeWidth: action.data.strokeWidth,
						fill: "",
						selectable: false,
						evented: false,
						objectId: action.objectId,
					} as any);
					canvas.add(path);
					canvas.renderAll();
				} else if (action.type === "circle") {
					const circle = new Circle({
						left: action.data.left,
						top: action.data.top,
						radius: action.data.radius,
						stroke: action.data.stroke,
						strokeWidth: action.data.strokeWidth,
						fill: "transparent",
						selectable: true,
						evented: true,
						objectId: action.objectId,
					} as any);
					canvas.add(circle);
					canvas.renderAll();
					} else if (action.type === "rect") {
					const rect = new Rect({
						left: action.data.left,
						top: action.data.top,
						width: action.data.width,
						height: action.data.height,
						stroke: action.data.stroke,
						strokeWidth: action.data.strokeWidth,
						fill: "transparent",
						selectable: true,
						evented: true,
						objectId: action.objectId,
					} as any);
					canvas.add(rect);
					canvas.renderAll();
				} else if (action.type === "triangle") {
					const triangle = new Triangle({
						left: action.data.left,
						top: action.data.top,
						width: action.data.width,
						height: action.data.height,
						stroke: action.data.stroke,
						strokeWidth: action.data.strokeWidth,
						fill: "transparent",
						selectable: true,
						evented: true,
						
						objectId: action.objectId,
					} as any);
					canvas.add(triangle);
					canvas.renderAll();
				} else if (action.type === "line" || action.type === "arrow") {
					const line = new Line([action.data.x1, action.data.y1, action.data.x2, action.data.y2], {
						stroke: action.data.stroke,
						strokeWidth: action.data.strokeWidth,
						selectable: true,
						evented: true,
						objectId: action.objectId,
					} as any);
					canvas.add(line);
					
					if (action.type === "arrow") {
						const angle = Math.atan2(
							action.data.y2 - action.data.y1,
							action.data.x2 - action.data.x1
						);
						const arrowSize = 15;

						const arrowHead = new Polygon(
							[
								{ x: 0, y: 0 },
								{ x: -arrowSize, y: arrowSize / 2 },
								{ x: -arrowSize, y: -arrowSize / 2 },
							],
							{
								fill: action.data.stroke,
								left: action.data.x2,
								top: action.data.y2,
								angle: (angle * 180) / Math.PI,
									selectable: true,
						evented: true,
								objectId: `${action.objectId}-head`,
							} as any
						);

						canvas.add(arrowHead);
					}
					
					canvas.renderAll();
				} else if (action.type === "text") {
					const text = new IText(action.data.text, {
						left: action.data.left,
						top: action.data.top,
						fontSize: action.data.fontSize,
						fill: action.data.fill,
						editable: true,
						selectable: true,
						evented: true,
						objectId: action.objectId,
					} as any);
					canvas.add(text);
					canvas.renderAll();
				} else if (action.type === "sticky") {
					// Create sticky note (rect + text)
					const rect = new Rect({
						left: action.data.left,
						top: action.data.top,
						width: action.data.width,
						height: action.data.height,
						fill: action.data.fill,
						stroke: action.data.stroke,
						strokeWidth: 2,
						selectable: true,
						evented: true,
						objectId: action.objectId,
					} as any);
					canvas.add(rect);
					canvas.renderAll();
				} else if (action.type === "image") {
					// Load image from URL (uploaded to server)
					if (action.data.src) {
						FabricImage.fromURL(action.data.src).then((img) => {
							img.set({
								left: action.data.left,
								top: action.data.top,
								scaleX: action.data.scaleX || 1,
								scaleY: action.data.scaleY || 1,
								angle: action.data.angle || 0,
									selectable: true,
						evented: true,
								objectId: action.objectId,
							} as any);
							canvas.add(img);
							canvas.renderAll();
						}).catch((err) => {
							console.error("Error loading remote image:", err);
						});
					}
				}
			} finally {
				isRemoteUpdate.current = false;
			}
		},
		[applyCanvasState]
	);
const handleParticipantConnected = () => {
			setTimeout(() => {
				const state = getCanvasState();
				if (state && state.objects.length > 0) {
					sendDrawingAction({
						type: "sync",
						data: state,
						timestamp: Date.now(),
					});
				}
			}, 1000);
		};
	

	useEffect(() => {
		if (!room || !isOpen) return;

		const handleDataReceived = (
			payload: Uint8Array,
			participant?: RemoteParticipant,
			kind?: DataPacket_Kind,
			topic?: string
		) => {
			if (topic !== "whiteboard") return;

			try {
				const decoder = new TextDecoder();
				const strData = decoder.decode(payload);
				const action: DrawingAction = JSON.parse(strData);

				applyRemoteDrawingAction(action);
			} catch (error) {
				console.error("Error processing received data:", error);
			}
		};

	

		room.on(RoomEvent.DataReceived, handleDataReceived);
		room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);

		return () => {
			room.off(RoomEvent.DataReceived, handleDataReceived);
			room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
		};
	}, [room, isOpen, applyRemoteDrawingAction, getCanvasState, sendDrawingAction]);
	useEffect(()=>{
handleParticipantConnected()
	},[isOpen])

	useEffect(() => {
		if (!isOpen) return;

		const canvas = new Canvas(canvasEl.current!, {
			backgroundColor: "#ffffff",
			selection: false,
			width: FIXED_CANVAS_WIDTH,
			height: FIXED_CANVAS_HEIGHT,
		});

		const brush = new PencilBrush(canvas);
		brush.color =color;
		brush.width = strokeWidth; 
		canvas.freeDrawingBrush = brush;

		// Set the canvas ref first
		canvasRef.current = canvas;

	
		// Restore persisted canvas state if it exists - do this after canvas is ready
		if (persistedCanvasState) {
			setTimeout(() => {
				applyCanvasState(persistedCanvasState);
				
				
			}, 0);
		} 

		canvas.on("path:created", (e: any) => {
			if (e.path) {
				const objectId = generateObjectId();
				e.path.set({
					selectable: false,
					evented: false,
					objectId: objectId,
				} as any);

				if (!isRemoteUpdate.current) {
					const pathObj = e.path.toObject() as any;
					const pathData = {
						path: pathObj.path,
						stroke: pathObj.stroke,
						strokeWidth: pathObj.strokeWidth,
					};
					sendDrawingAction({
						type: "path",
						data: pathData,
						objectId: objectId,
						timestamp: Date.now(),
					});
				}
			}
		});

		canvas.on("object:modified", (e: any) => {
			if (!e.target || isRemoteUpdate.current) return;

			const obj = e.target;
			const objectId = (obj as any).objectId;

			if (objectId) {
				const data: any = {
					left: obj.left,
					top: obj.top,
					scaleX: obj.scaleX,
					scaleY: obj.scaleY,
					angle: obj.angle,
				};

				if (obj.type === "circle") {
					data.radius = (obj as Circle).radius;
				} else if (obj.type === "rect") {
					data.width = (obj as Rect).width;
					data.height = (obj as Rect).height;
				} else if (obj.type === "i-text") {
					data.text = (obj as IText).text;
					data.fontSize = (obj as IText).fontSize;
				}

				sendDrawingAction({
					type: "modify",
					objectId: objectId,
					data: data,
					timestamp: Date.now(),
				});
			}
		});

		canvas.on("object:moving", (e: any) => {
			if (!e.target || isRemoteUpdate.current) return;

			const obj = e.target;
			const objectId = (obj as any).objectId;

			if (objectId) {
				sendDrawingActionThrottled(
					{
						type: "modify",
						objectId: objectId,
						data: {
							left: obj.left,
							top: obj.top,
						},
						timestamp: Date.now(),
					},
					50
				);
			}
		});

		canvas.on("object:scaling", (e: any) => {
			if (!e.target || isRemoteUpdate.current) return;

			const obj = e.target;
			const objectId = (obj as any).objectId;

			if (objectId) {
				sendDrawingActionThrottled(
					{
						type: "modify",
						objectId: objectId,
						data: {
							scaleX: obj.scaleX,
							scaleY: obj.scaleY,
						},
						timestamp: Date.now(),
					},
					50
				);
			}
		});

		canvas.on("object:rotating", (e: any) => {
			if (!e.target || isRemoteUpdate.current) return;

			const obj = e.target;
			const objectId = (obj as any).objectId;

			if (objectId) {
				sendDrawingActionThrottled(
					{
						type: "modify",
						objectId: objectId,
						data: {
							angle: obj.angle,
						},
						timestamp: Date.now(),
					},
					50
				);
			}
		});

		canvas.on("text:changed", (e: any) => {
			if (!e.target || isRemoteUpdate.current) return;

			const obj = e.target as IText;
			const objectId = (obj as any).objectId;

			if (objectId) {
				sendDrawingActionThrottled(
					{
						type: "modify",
						objectId: objectId,
						data: {
							text: obj.text,
						},
						timestamp: Date.now(),
					},
					200
				);
			}
		});

		const resizeCanvas = () => {
			const containerWidth = window.innerWidth * 0.9;
			const containerHeight = window.innerHeight * 0.8;

			const scaleX = containerWidth / FIXED_CANVAS_WIDTH;
			const scaleY = containerHeight / FIXED_CANVAS_HEIGHT;
			const scale = Math.min(scaleX, scaleY);

			canvas.setZoom(scale);

			canvas.setWidth(FIXED_CANVAS_WIDTH * scale);
			canvas.setHeight(FIXED_CANVAS_HEIGHT * scale);

			canvas.renderAll();
		};

		resizeCanvas();
		window.addEventListener("resize", resizeCanvas);

		return () => {
			window.removeEventListener("resize", resizeCanvas);
			throttleTimers.current.forEach((timer) => clearTimeout(timer));
			throttleTimers.current.clear();
			canvas.dispose();
		};
	}, [isOpen, generateObjectId, sendDrawingAction, sendDrawingActionThrottled, persistedCanvasState, applyCanvasState]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || !canvas.freeDrawingBrush) return;
		const brush = canvas.freeDrawingBrush as PencilBrush;
		brush.color = color;
		brush.width = strokeWidth;
	}, [color, strokeWidth]);

	

	



	

	

	
	

	return (
		<>
			<Modal
				open={isOpen}
				onCancel={handleCloseModal}
				centered={false}
				width="100vw"
				rootClassName="responsive-draw-modal"
				maskStyle={{ backgroundColor: "rgba(0,0,0,0.45)" }}
				height="92vh"
				maskClosable={false}
				keyboard={false}
				closeIcon={
					null
				}
				footer={
					null
				}
				style={{
					top: 20,
					padding: 0,
					margin: 0,
					maxWidth: "100vw",
					height: "92vh",
				}}
				styles={{
					body: {
						padding: 0,
						margin: 0,
						background: "#fafafa",
						borderRadius: 0,
						height: "calc(92vh - 70px)",
						overflow: "hidden",
					},
				
				}}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "stretch",
						height: "100%",
						width: "100%",
					}}
				>
			

					{/* Full Canvas Container */}
					<div
						style={{
							flex: 1,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
							height: "100%",
							background: "#fafafa",
							overflow: "hidden",
						}}
					>
						<canvas
							ref={canvasEl}
							style={{
								display: "block",
								boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
							}}
						/>
					</div>
				</div>
			</Modal>

		</>
	);
};

export default WhiteBoard;
